import { Trans, useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiBookmarkCheckOutline, mdiBookmarkOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import type { EntityLog } from '../../api/schema'
import { useUser } from '../../api/queries'
import { formatDisplayNumber } from '../../lib/format'
import { useCanOpenRunDetails } from '../agent-runs/can-open-run'

// Shared rendering pieces for the entity activity feed. Both the per-asset feed
// (AssetActivityLog) and the organization-wide feed (GlobalActivityLog) render
// the exact same row via <ActivityEntry> so the two stay in lockstep.

// ActivityUserName renders the author's name by looking up the user by uid.
// The activity log projection ships only user_uid, so we resolve via the
// users-by-uid query. React Query dedupes repeated lookups within a page.
//
// When the uid resolves, the name links to the user's page — users are surfaced
// as "Contacts" in the nav, and the author is as much a clickable entity as the
// row's subject. System rows (no uid) stay plain text.
export function ActivityUserName({ org, userUid, fallback }: { org: string; userUid?: string | null; fallback: string }) {
  const { data: user } = useUser(org, userUid || '')
  if (!userUid) return <>{fallback}</>
  const label = user?.name || fallback
  return (
    <Link
      to="/$org/users/$userUid"
      params={{ org, userUid }}
      className="hover:underline"
      data-testid="activity-user-link"
    >
      {label}
    </Link>
  )
}

export function formatRelative(dateStr: string, locale: string): string {
  const date = new Date(dateStr)
  const diffMs = date.getTime() - Date.now()
  const diffSec = Math.round(diffMs / 1000)
  const abs = Math.abs(diffSec)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  if (abs < 60) return rtf.format(diffSec, 'second')
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour')
  if (abs < 2592000) return rtf.format(Math.round(diffSec / 86400), 'day')
  if (abs < 31536000) return rtf.format(Math.round(diffSec / 2592000), 'month')
  return rtf.format(Math.round(diffSec / 31536000), 'year')
}

// localizedName resolves a {en, fr, ...} name map to a display string using
// the locale → en → fr fallback chain used throughout the activity feed.
export function localizedName(name: Record<string, string> | undefined | null, locale: string): string | undefined {
  if (!name) return undefined
  return name[locale] || name.en || name.fr
}

export function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string') return value
  // One decimal maximum for anything numeric the reader sees — a property
  // change logged as 42.333333333333336 is noise, not provenance.
  if (typeof value === 'number') return formatDisplayNumber(value)
  if (typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

export function operationBadgeClass(op: string): string {
  switch (op) {
    case 'INSERT':
      return 'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30'
    case 'UPDATE':
      return 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30'
    case 'DELETE':
      return 'bg-destructive/15 text-destructive border-destructive/30'
    case 'RESTORE':
      return 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30'
    default:
      return ''
  }
}

// EntityLink wraps its children in a router Link to the best destination for
// the row, or renders them as-is when nothing resolves.
//
// Three tables address their own detail page via entity_uid (assets, documents,
// issues). The rest can't: an asset_data / pages / tags_entities /
// asset_external_ids uid identifies a child or join row with no page of its
// own, and a contract_assets uid identifies the asset↔contract join. Those rely
// on the refs the backend hydrates — the owning asset (from search_asset_id),
// the contract, or the tag — so every row in the feed leads somewhere.
function EntityLink({ org, entry, children, className }: { org: string; entry: EntityLog; children: React.ReactNode; className?: string }) {
  const uid = entry.entity_uid
  const assetUid = entry.asset?.uid
  const testId = 'activity-entity-link'

  switch (entry.entity_table) {
    case 'assets':
      if (uid) {
        return (
          <Link to="/$org/assets/$assetUid" params={{ org, assetUid: uid }} className={className} data-testid={testId}>
            {children}
          </Link>
        )
      }
      break
    case 'documents':
      if (uid) {
        return (
          <Link to="/$org/documents/$docUid" params={{ org, docUid: uid }} className={className} data-testid={testId}>
            {children}
          </Link>
        )
      }
      break
    case 'issues':
      if (uid) {
        return (
          <Link to="/$org/issues/$uid" params={{ org, uid }} className={className} data-testid={testId}>
            {children}
          </Link>
        )
      }
      break
    case 'contract_assets':
      if (entry.contract?.uid) {
        return (
          <Link to="/$org/contracts/$contractUid" params={{ org, contractUid: entry.contract.uid }} className={className} data-testid={testId}>
            {children}
          </Link>
        )
      }
      break
    case 'finance_lines':
      if (uid) {
        return (
          <Link to="/$org/expenses/$expenseUid" params={{ org, expenseUid: uid }} className={className} data-testid={testId}>
            {children}
          </Link>
        )
      }
      break
    case 'tags_entities':
      // The tag route resolves its param as a code *or* a uid.
      if (entry.tag?.uid) {
        return (
          <Link to="/$org/settings/tags/$tagId" params={{ org, tagId: entry.tag.uid }} className={className} data-testid={testId}>
            {children}
          </Link>
        )
      }
      break
    case 'asset_data':
      // Land on the asset with the property's history drawer open. The
      // Properties surface lives on the Data tab for site/building/lot —
      // AssetDetailPage resolves `properties` onto it there.
      if (assetUid && entry.property?.uid) {
        return (
          <Link
            to="/$org/assets/$assetUid"
            params={{ org, assetUid }}
            search={{ tab: 'properties', propertyHistory: entry.property.uid }}
            className={className}
            data-testid={testId}
          >
            {children}
          </Link>
        )
      }
      break
    case 'pages':
      if (assetUid) {
        return (
          <Link to="/$org/assets/$assetUid" params={{ org, assetUid }} search={{ tab: 'activity' }} className={className} data-testid={testId}>
            {children}
          </Link>
        )
      }
      break
  }

  // Anything asset-scoped that didn't resolve a more precise target (notably
  // asset_external_ids) still links to its asset.
  if (assetUid) {
    return (
      <Link to="/$org/assets/$assetUid" params={{ org, assetUid }} className={className} data-testid={testId}>
        {children}
      </Link>
    )
  }
  return <>{children}</>
}

/**
 * tokenAttribution names the machine token behind an entry: an agent run reads
 * "via <name>" without the word token (spec 2026-10-21-19 §5), any other named
 * token "via token <name>". Nothing for a session or a system write.
 */
export function tokenAttribution(
  entry: Pick<EntityLog, 'token_name' | 'token_type' | 'agent_run_uid'>
): { kind: 'agent_run'; name: string; runUid?: string } | { kind: 'token'; name: string } | undefined {
  if (entry.token_name == null) return undefined
  if (entry.token_type === 'agent_run') {
    return { kind: 'agent_run', name: entry.token_name, runUid: entry.agent_run_uid ?? undefined }
  }
  return { kind: 'token', name: entry.token_name }
}

/**
 * onBehalfAttribution is the person an assistant run acted for, with its
 * mission and model (spec 2026-10-21-24 §9), or undefined for any other entry.
 */
export function onBehalfAttribution(
  entry: Pick<EntityLog, 'token_type' | 'on_behalf_of' | 'agent_mission' | 'agent_model'>
): { person: string; mission: string; model: string } | undefined {
  if (entry.token_type !== 'agent_run' || !entry.on_behalf_of) return undefined
  return { person: entry.on_behalf_of.name, mission: entry.agent_mission ?? '', model: entry.agent_model ?? '' }
}

/** AgentOnBehalf reads "Agent Buildtory for Marie Dupont · assistant · claude-opus-5-5", linked to the run. */
function AgentOnBehalf({ org, userUid, values, runUid }: {
  org: string
  userUid?: string | null
  values: { person: string; mission: string; model: string }
  runUid?: string
}) {
  const { t } = useTranslation('assets')
  const { data: user } = useUser(org, userUid || '')
  const label = t('activity.agent_on_behalf', { ...values, agent: user?.name || t('activity.system_user') })
  return (
    <span className="text-muted-foreground" data-testid="activity-entry-agent-on-behalf">
      {runUid ? (
        <Link to="/$org/agent-runs/$runUid" params={{ org, runUid }} className="hover:underline"
          data-testid="activity-entry-agent-run-link">
          {label}
        </Link>
      ) : label}
    </span>
  )
}

/** agentRunLink: the run an agent entry links to, only for a viewer who may open it. */
export function agentRunLink(attribution: ReturnType<typeof tokenAttribution>, canOpenRun: boolean): string | undefined {
  if (attribution?.kind !== 'agent_run' || !canOpenRun) return undefined
  return attribution.runUid
}

interface ActivityEntryProps {
  entry: EntityLog
  org: string
  locale: string
  // When true, the entity label links to the entity's detail page (used by the
  // organization-wide feed). The per-asset feed already lives on the entity, so
  // it renders the label as plain text.
  linkEntity?: boolean
  // When true, the row offers a resume marker — a link that pins the feed to
  // this entry via the `cursor` search param, so a deep scroll position is
  // bookmarkable. Only the organization-wide feed paginates far enough to need
  // it; the per-asset feed omits it.
  showMarker?: boolean
  // True when this entry is the one the feed is currently resumed from.
  isMarked?: boolean
}

export function ActivityEntry({ entry, org, locale, linkEntity = false, showMarker = false, isMarked = false }: ActivityEntryProps) {
  const { t } = useTranslation('assets')
  const canOpenRun = useCanOpenRunDetails()
  const attribution = tokenAttribution(entry)
  const runLink = agentRunLink(attribution, canOpenRun)
  const onBehalf = onBehalfAttribution(entry)

  const entityLabelKey = `activity.entity_${entry.entity_table}`
  const baseEntityLabel = t(entityLabelKey, { defaultValue: entry.entity_table })
  // asset_data rows carry a `property` ref; tags_entities a `tag` ref;
  // asset_external_ids a hydrated {source_code, value} on both sides.
  const propertyRef = (entry as unknown as { property?: { uid: string; name: Record<string, string> } }).property
  const propertyName = localizedName(propertyRef?.name, locale)
  const tagRef = (entry as unknown as { tag?: { uid: string; name: string } }).tag
  const documentRef = (entry as unknown as { document?: { uid: string; name: string } }).document
  // asset_frame_registrations rows carry the plan (or asset frame) that was
  // registered, so the feed can say *which* plan instead of "Plan registration".
  const frameRef = (entry as unknown as {
    frame_registration?: { source_kind: string; source_uid?: string; source_name?: string; target_kind: string }
  }).frame_registration
  const frameSourceName = frameRef?.source_name || undefined
  const extIdRef = (entry as unknown as {
    external_id_new?: { source_code?: string; value?: string }
    external_id_old?: { source_code?: string; value?: string }
  })
  const extIdSide = extIdRef.external_id_new || extIdRef.external_id_old
  const extIdDisplay = extIdSide && (extIdSide.source_code || extIdSide.value)
    ? [extIdSide.source_code, extIdSide.value].filter(Boolean).join(': ')
    : undefined
  // documentRef's name is only needed for UPDATE/DELETE/RESTORE rows — INSERT
  // already renders the name via the summary line below, and duplicating it
  // in the header would show it twice.
  const documentName = entry.operation !== 'INSERT' ? documentRef?.name : undefined
  const frameName = entry.operation !== 'INSERT' ? frameSourceName : undefined
  const refName = propertyName || tagRef?.name || documentName || extIdDisplay || frameName
  const entityLabel = refName ? `${baseEntityLabel}: ${refName}` : baseEntityLabel
  const opKey = `activity.operation_${entry.operation.toLowerCase()}`

  const fields = entry.changed_fields || []
  const oldVal = (entry.entity_old_value || {}) as Record<string, unknown>
  const newVal = (entry.entity_new_value || {}) as Record<string, unknown>

  // One-line summary for existence events.
  let summary: string | null = null
  if (entry.operation === 'INSERT') {
    if (entry.entity_table === 'asset_data' && 'value' in newVal) {
      summary = `${entityLabel}: ${formatValue(newVal['value'])}`
    } else if (entry.entity_table === 'asset_external_ids') {
      summary = entityLabel
    } else if (entry.entity_table === 'asset_frame_registrations') {
      summary = frameSourceName ? `${entityLabel}: ${frameSourceName}` : entityLabel
    } else {
      const name =
        (newVal['name'] as string) ||
        (newVal['title'] as string) ||
        (newVal['file_name'] as string) ||
        null
      summary = name ? `${entityLabel}: ${name}` : entityLabel
    }
  } else if (entry.operation === 'DELETE' || entry.operation === 'RESTORE') {
    summary = entityLabel
  }

  // asset_type_id / document_type_id are internal numeric PKs — render the
  // localized type name from the hydrated refs instead, falling back to the
  // raw value when a ref is missing (e.g. the type was since hard-deleted).
  const typeRefsByField: Record<string, { old?: { name?: Record<string, string> } | null; new?: { name?: Record<string, string> } | null }> = {
    asset_type_id: { old: entry.asset_type_old, new: entry.asset_type_new },
    document_type_id: { old: entry.document_type_old, new: entry.document_type_new },
  }
  // asset_frame_registrations stores `transform` and `calibration` as jsonb.
  // Raw jsonb must never reach the DOM, so both are summarized into a short
  // human line instead of being JSON.stringify-ed by formatValue.
  function frameValueSummary(field: string, value: unknown): string {
    if (value === null || value === undefined || typeof value !== 'object') return '—'
    const v = value as Record<string, unknown>
    const num = (k: string): number | undefined => (typeof v[k] === 'number' ? (v[k] as number) : undefined)
    if (field === 'calibration') {
      const mm = num('mm_per_unit')
      if (mm === undefined) return '—'
      return t('activity.frame_scale_value', { value: formatDisplayNumber(mm) })
    }
    // transform: a planar similarity, or a wgs84 geo-anchor.
    const parts: string[] = []
    const lat = num('lat')
    const lon = num('lon')
    if (lat !== undefined && lon !== undefined) {
      parts.push(t('activity.frame_geo_value', { lat: formatDisplayNumber(lat), lon: formatDisplayNumber(lon) }))
      const north = num('north_angle_deg')
      if (north !== undefined) parts.push(t('activity.frame_north_value', { value: formatDisplayNumber(north) }))
    } else {
      const tx = num('tx')
      const ty = num('ty')
      if (tx !== undefined || ty !== undefined) {
        parts.push(`x ${formatDisplayNumber(tx ?? 0)}, y ${formatDisplayNumber(ty ?? 0)}`)
      }
      const rotation = num('rotation_deg')
      if (rotation) parts.push(t('activity.frame_rotation_value', { value: formatDisplayNumber(rotation) }))
    }
    const scale = num('scale')
    if (scale !== undefined && scale !== 1) {
      parts.push(t('activity.frame_zoom_value', { value: formatDisplayNumber(scale) }))
    }
    return parts.length > 0 ? parts.join(' · ') : '—'
  }

  function diffCellValue(field: string, side: 'old' | 'new'): string {
    const refs = typeRefsByField[field]
    const rawValue = side === 'old' ? oldVal[field] : newVal[field]
    if (refs) {
      const name = localizedName(refs[side]?.name, locale)
      if (name) return name
    }
    if (entry.entity_table === 'asset_frame_registrations') {
      return frameValueSummary(field, rawValue)
    }
    return formatValue(rawValue)
  }

  const labelNode = <span className="font-medium text-foreground">{entityLabel}</span>

  const markerLabel = isMarked ? t('activity.marker_clear') : t('activity.marker_set')

  return (
    <li
      key={entry.uid}
      data-testid="activity-entry"
      data-marked={isMarked ? 'true' : undefined}
      className={`border rounded-md p-4 bg-background ${isMarked ? 'border-primary ring-1 ring-primary/40' : 'border-border'}`}
    >
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <Badge variant="outline" className={operationBadgeClass(entry.operation)}>
          {t(opKey)}
        </Badge>
        {linkEntity ? (
          <EntityLink org={org} entry={entry} className="font-medium text-foreground hover:underline">
            {entityLabel}
          </EntityLink>
        ) : (
          labelNode
        )}
        <span className="text-muted-foreground">·</span>
        {onBehalf ? (
          <AgentOnBehalf org={org} userUid={entry.user_uid} values={onBehalf} runUid={runLink} />
        ) : (
          <span className="text-muted-foreground">
            <ActivityUserName org={org} userUid={entry.user_uid} fallback={t('activity.system_user')} />
          </span>
        )}
        {attribution?.kind === 'agent_run' && !onBehalf && (
          <span className="text-muted-foreground" data-testid="activity-entry-agent-run">
            <Trans
              t={t}
              i18nKey="activity.via_agent_run"
              values={{ name: attribution.name }}
              components={{
                run: runLink ? (
                  <Link
                    to="/$org/agent-runs/$runUid"
                    params={{ org, runUid: runLink }}
                    className="hover:underline"
                    data-testid="activity-entry-agent-run-link"
                  />
                ) : (
                  <span />
                ),
              }}
            />
          </span>
        )}
        {attribution?.kind === 'token' && (
          <span className="text-muted-foreground" data-testid="activity-entry-token">
            {t('activity.via_token', { name: attribution.name })}
          </span>
        )}
        {entry.client_ip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="text-muted-foreground cursor-help text-xs font-mono"
                data-testid="activity-entry-client-ip"
              >
                {entry.client_ip}
              </span>
            </TooltipTrigger>
            <TooltipContent>{t('activity.client_ip')}</TooltipContent>
          </Tooltip>
        )}
        <span className="text-muted-foreground">·</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-muted-foreground cursor-help">
              {formatRelative(entry.created_at, locale)}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {new Date(entry.created_at).toLocaleString(locale)}
          </TooltipContent>
        </Tooltip>

        {showMarker && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/$org/activity"
                params={{ org }}
                // Merge into the existing search so the marker keeps the
                // active filters; clicking a set marker clears it.
                search={prev => ({ ...prev, cursor: isMarked ? undefined : entry.created_at })}
                aria-label={markerLabel}
                data-testid="activity-entry-marker"
                className={`ml-auto shrink-0 rounded p-1 hover:bg-muted ${isMarked ? 'text-primary' : 'text-muted-foreground'}`}
              >
                {isMarked ? <AppIcon path={mdiBookmarkCheckOutline} size="sm" /> : <AppIcon path={mdiBookmarkOutline} size="sm" />}
              </Link>
            </TooltipTrigger>
            <TooltipContent>{markerLabel}</TooltipContent>
          </Tooltip>
        )}
      </div>

      {entry.operation === 'UPDATE' && fields.length > 0 && (
        <div className="mt-3">
          <Table data-testid="activity-diff-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('activity.field')}</TableHead>
                <TableHead>{t('activity.old_value')}</TableHead>
                <TableHead>{t('activity.new_value')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map(field => (
                <TableRow key={field}>
                  <TableCell className="font-mono text-xs">
                    {t(`activity.field_${field}`, { defaultValue: field })}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs max-w-xs truncate">
                    {diffCellValue(field, 'old')}
                  </TableCell>
                  <TableCell className="text-xs max-w-xs truncate">
                    {diffCellValue(field, 'new')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {summary && (
        <div className="mt-2 text-sm text-muted-foreground">{summary}</div>
      )}
    </li>
  )
}
